<template>
  <div
    class="relative"
    @contextmenu.stop="onContextMenu"
    :class="[!item.data.visible && 'visible' in item.data ? 'text-gray-700' : 'text-gray-200']">
    <div
      :draggable="true"
      @dragover="handleDragover"
      @dragstart="handleDragstart"
      @dragleave="handleDragleave"
      @click="handleClick"
      @dblclick="handleDblclick"
      @drop="handleDrop"
      @dragend="handleDragend"
      class="group hover:border-primary relative flex items-center rounded border-transparent"
      :class="[
        selectionStore.selectedObjectId === item.code ? 'bg-primary/10' : '',
        appStore.treeDisplayOnlyIcon ? 'mx-1 h-10 justify-center p-0.5' : 'h-12 cursor-pointer p-2',
      ]">
      <div
        v-if="item.data.lock"
        class="pointer-events-none absolute inset-0 rounded"
        style="
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 4px,
            rgba(255, 109, 240, 0.3) 4px,
            rgba(255, 109, 240, 0.3) 5px
          );
          background-color: rgba(0, 0, 0, 0.2);
        "></div>

      <template v-if="appStore.treeDisplayOnlyIcon">
        <div class="relative mt-0.5 cursor-pointer rounded-md p-2">
          <component
            :is="itemIcon()"
            :size="18"
            class="group-hover:text-primary relative transition-all duration-500 ease-in-out"
            :stroke-width="[selectionStore.selectedObjectId === item.code ? 3 : 2]"
            :class="[
              selectionStore.selectedObjectId === item.code && appStore.treeDisplayOnlyIcon
                ? 'text-primary'
                : 'text-surface-400',
            ]"
            v-tooltip="item.name || null" />
        </div>
      </template>
      <template v-else>
        <div class="flex min-w-0 flex-auto" :style="'padding-left:' + 16 * depth + 'px'">
          <div class="w-4 flex-none items-center">
            <div
              v-if="hasChildren || item.type === 'model'"
              @click="toggleExpand"
              :class="[isExpanded ? ' ' : '-rotate-90']"
              class="pi pi-sort-down-fill cursor-pointer !text-xs text-gray-600"></div>
          </div>
          <div class="flex-auto overflow-hidden">
            <div class="flex w-full items-center space-x-2">
              <div class="flex items-center">
                <component :is="itemIcon()" size="16" class="text-surface-400" />
              </div>
              <div class="truncate text-nowrap">
                {{ item.name ?? 'Object' }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex gap-1" v-if="!appStore.treeDisplayOnlyIcon && !selectionStore.selectedObjectId">
          <DButton
            text
            size="small"
            class="group-hover:text-primary w-auto p-1.5"
            :class="item.data.visible || !('visible' in item.data) ? 'text-gray-500' : ''"
            @click="
              (event) => {
                event.stopPropagation();
                if (!item.properties) {
                  item.properties = {};
                }
                updateNode(item.code, { visible: !item.data.visible });
              }
            ">
            <LeftPanelEyeIcon :item="item" :size="14" stroke-width="1.8" />
          </DButton>

          <DButton
            text
            size="small"
            class="group-hover:text-primary w-auto p-1.5"
            :class="item.data.lock ? '' : 'text-gray-500'"
            v-tooltip="'Lock/Unlock Object'"
            @click="
              (event) => {
                event.stopPropagation();
                updateNode(item.code, { lock: !item.data.lock });
              }
            ">
            <LeftPanelLockIcon :item="item" :size="14" stroke-width="1.8" />
          </DButton>
        </div>
      </template>
    </div>

    <div
      v-if="isExpanded"
      :class="{
        'ml-4': hasParent,
      }">
      <SelectionItem
        v-for="child in cruxModuleStore.getSceneTreeModules(item.code)"
        :key="child.code"
        :item="child"
        :depth="depth + 1"
        @contextmenu="(e, code) => emit('contextmenu', e, code)" />
    </div>

    <!-- Show meshes for model items -->
    <div v-if="item.type === 'model' && isExpanded && !appStore.treeDisplayOnlyIcon">
      <div
        v-if="modelMeshes.length === 0"
        class="mx-2 px-2 text-sm text-gray-400"
        :style="'padding-left:' + 8 * (depth + 1) + 'px'">
        No meshes found in this model
      </div>
      <MeshItem
        v-for="mesh in modelMeshes"
        :key="mesh.objectId"
        :objectId="item.code"
        :mesh-data="mesh"
        :depth="depth" />
    </div>
  </div>
</template>

<script setup>
import { SelectionItem } from '@/components';
import { useNodeManager } from '@/composables/useNodeManager';
import { PANEL_ICONS } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useAppStore } from '@/stores/appStore';
import { useSelectionStore } from '@/stores/ui/selection';
import { getObjectByCode, zoomToObject } from '@/threejs/objectManager';
import { DButton } from '@disguise/cloud-ui';
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import MeshItem from './MeshItem.vue';
import LeftPanelEyeIcon from './partial/LeftPanelEyeIcon.vue';
import LeftPanelLockIcon from './partial/LeftPanelLockIcon.vue';

// Global drag state shared across all instances
const globalDragState = ref(false);

const selectionStore = useSelectionStore();
const cruxModuleStore = useCruxModuleStore();
const { updateNode, moveNode } = useNodeManager();
const appStore = useAppStore();
const templateRef = useTemplateRef('templateRef');

const props = defineProps({
  item: Object,
  depth: { type: Number, default: 0 },
});

const emit = defineEmits(['contextmenu']);

const onContextMenu = (e) => {
  e.preventDefault();
  emit('contextmenu', e, props.item.code);
};

const isExpanded = ref(false);
const isBeingDraggedOver = ref(false);

const hasChildren = computed(() => {
  return cruxModuleStore.getSceneTreeModules(props.item.code).length > 0;
});

// Function to get UV names from an object
function getUVNamesFromObject(object) {
  const uvNames = [];

  // Check if object has geometry with UV attributes
  if (object.geometry && object.geometry.attributes) {
    const attributes = object.geometry.attributes;

    // Look for UV-related attributes
    Object.keys(attributes).forEach((key) => {
      if (key.toLowerCase().includes('uv')) {
        uvNames.push(key);
      }
    });
  }

  return uvNames;
}

// Function to collect mesh data from a model object
function collectMeshDataFromModel(object, meshDataList = [], first = false) {
  if (!object) return meshDataList;

  // If this is a mesh, add it to the list
  if (object.isMesh && !first) {
    const uvNames = getUVNamesFromObject(object);
    meshDataList.push({
      objectId: object.uuid,
      name: object.userData?.name || object.name || `Mesh ${meshDataList.length + 1}`,
      uvNames: uvNames,
      visible: object.visible !== false,
    });
  }

  // Recursively process children
  if (object.children && object.children.length > 0) {
    object.children.forEach((child) => {
      collectMeshDataFromModel(child, meshDataList);
    });
  }

  return meshDataList;
}

// Reactive trigger to re-evaluate modelMeshes when a model finishes async loading
const modelLoadTick = ref(0);
const onModelLoaded = (e) => {
  if (e.detail?.code === props.item.code) {
    modelLoadTick.value++;
  }
};
onMounted(() => window.addEventListener('modelLoaded', onModelLoaded));
onBeforeUnmount(() => window.removeEventListener('modelLoaded', onModelLoaded));

// Computed property to get mesh data for model items
const modelMeshes = computed(() => {
  if (props.item.type !== 'model') return [];
  modelLoadTick.value; // reactive dependency on model load

  const object = getObjectByCode(props.item.code);
  if (!object) {
    return [];
  }

  return collectMeshDataFromModel(object, [], true);
});

const toggleExpand = (e) => {
  e?.stopPropagation();

  if (hasChildren.value || props.item.type === 'model') {
    isExpanded.value = !isExpanded.value;
  }
};

watch(
  [() => selectionStore.selectedObjectId, () => cruxModuleStore.cruxModules],
  ([objectId, cruxModules]) => {
    if (objectId) {
      if (objectId !== props.item.code) {
        // isExpanded.value = false
        return;
      }
      const node = cruxModuleStore.cruxModules.find((n) => n.code === objectId);

      if (node) {
        isExpanded.value = true;
      } else {
        isExpanded.value = false;
      }
    }
  },
  { immediate: true }
);
const isGroup = computed(() => props.item.type === 'group');
const hasParent = computed(() => !!props.item?.data?.parentCode);

const itemIcon = () => {
  let type = props.item.type === 'geometry' ? props.item.data.shape || 'box' : props.item.type || 'folder';
  return PANEL_ICONS[type];
};
const handleClick = () => {
  selectionStore.setSelectedObject(props.item.code);
  isExpanded.value = true;
};

const backgroundEffects = (set = true) => {
  const classList = ['bg-primary/20'];

  // Find all group elements in the tree and highlight them
  const allGroupElements = document.querySelectorAll('[data-is-group="true"]');
  allGroupElements.forEach((element) => {
    if (set) {
      element.classList.add(...classList);
    } else {
      element.classList.remove(...classList);
    }
  });
};

const handleDblclick = () => {
  zoomToObject(props.item.code);
};
const handleDragstart = (e) => {
  globalDragState.value = true;
  backgroundEffects();
  const { parentCode } = props.item?.data ?? {};
  if (parentCode) {
    appStore.setTreeParentDragOver(true);
  }
  e.dataTransfer.setData('text/plain', props.item.code);
};

const handleDragend = (e) => {
  globalDragState.value = false;
  const { parentCode } = props.item?.data ?? {};
  if (parentCode) {
    appStore.setTreeParentDragOver(false);
  }
  e.dataTransfer.setData('text/plain', props.item.code);
  backgroundEffects(false);
};

const handleDragover = (e) => {
  e.preventDefault();

  if (isGroup.value) {
    isBeingDraggedOver.value = true;
  }
};

const handleDragleave = (e) => {
  // Only reset if we're actually leaving the element (not entering a child)
  const rect = e.currentTarget.getBoundingClientRect();
  const x = e.clientX;
  const y = e.clientY;

  if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
    isBeingDraggedOver.value = false;

    // Reset background color for tree object when leaving group
    if (isGroup.value) {
      templateRef.value?.classList.remove('bg-primary/20');
    }
  }
};

const handleDrop = async (e) => {
  e.preventDefault();

  const draggedItemCode = e.dataTransfer.getData('text/plain');
  if (props.item.code === draggedItemCode) return;

  // Move to this group
  if (isGroup.value) {
    try {
      await moveNode(draggedItemCode, props.item.code);
    } catch (error) {
      console.error('Failed to move node:', error);
    } finally {
      isBeingDraggedOver.value = false;
      globalDragState.value = false;
      backgroundEffects(false);
    }
  } else {
    // If not a group, still clean up the drag state
    isBeingDraggedOver.value = false;
    globalDragState.value = false;
    backgroundEffects(false);
  }
};
</script>
